import Link from 'next/link';
import { Image } from 'react-datocms';
import TealWave from '@/components/TealWave';
import { MarkdownToJSX } from '@/components/MarkdownToJSX';
import type { AuthorForContributorCardFragment } from '@/utils/dato/generated';

const ChevronCircleIcon = ({ arrowColor = '#0E2C27', bgColor = '#71E6C0' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" aria-hidden="true" height="14" fill="none" width="14">
    <circle fill={bgColor} cx="7" cy="7" r="7" />
    <path
      d="M9.30078 6.86851L6.32115 9.84814L5.6259 9.1529L7.91028 6.86851L5.6259 4.58412L6.32115 3.88887L9.30078 6.86851Z"
      fill={arrowColor}
    />
  </svg>
);

const ViewProfileLink = ({ bgColor }: { bgColor?: string }) => (
  <span className="flex items-center gap-2 text-sm text-base-content">
    <ChevronCircleIcon bgColor={bgColor} />
    <span className="underline">View profile</span>
  </span>
);

const AuthorCard = ({ author }: { author: AuthorForContributorCardFragment }) => {
  if (!author.slug) {
    return null;
  }

  return (
    <Link
      className="flex flex-col bg-quarterary rounded-xl overflow-hidden hover:opacity-90 transition-opacity no-underline"
      href={`/blog/author/${author.slug}/`}
    >
      <div className="flex flex-col gap-4 p-6 sm:flex-row sm:gap-6">
        {author.avatar?.cardImage ? (
          <div className="shrink-0">
            <Image data={author.avatar.cardImage} />
          </div>
        ) : null}
        <div className="flex flex-col gap-1 shrink-0 sm:min-w-[160px]">
          <p className="font-serif font-semibold text-lg">{author.name}</p>
          {author.jobTitle ? <p className="text-sm text-base-content/70">{author.jobTitle}</p> : null}
        </div>
        <div className="flex flex-col gap-3">
          {author.bio ? (
            <div className="text-sm text-base-content leading-relaxed line-clamp-3">
              <MarkdownToJSX md={author.bio} />
            </div>
          ) : null}
          <ViewProfileLink bgColor="white" />
        </div>
      </div>
      <TealWave />
    </Link>
  );
};

const ContributorRow = ({ author }: { author: AuthorForContributorCardFragment }) => {
  if (!author.slug) {
    return null;
  }

  return (
    <Link
      className="flex items-center justify-between py-4 hover:opacity-80 transition-opacity no-underline"
      href={`/blog/author/${author.slug}/`}
    >
      <div className="flex items-center gap-4">
        {author.avatar?.contributorImage ? (
          <div className="shrink-0">
            <Image data={author.avatar.contributorImage} />
          </div>
        ) : null}
        <p className="font-medium">{author.name}</p>
      </div>
      <ViewProfileLink />
    </Link>
  );
};

type Props = {
  contributors?: AuthorForContributorCardFragment[];
  author?: AuthorForContributorCardFragment | null;
};

const AuthorCards = ({ contributors, author }: Props) => {
  if (!author && !contributors?.length) {
    return null;
  }

  return (
    <div className="flex flex-col mt-10 mb-8 px-5 mx-auto max-w-[648px]">
      <div className="border-t border-base-100" />

      {author ? (
        <div className="py-8">
          <h2 className="font-semibold text-lg mb-4">Author</h2>
          <AuthorCard author={author} />
        </div>
      ) : null}

      {contributors && contributors.length > 0 ? (
        <div className="py-8">
          <h2 className="font-semibold text-lg mb-2">Contributors</h2>
          <div>
            {contributors.map((contributor) => (
              <ContributorRow key={contributor.id} author={contributor} />
            ))}
          </div>
        </div>
      ) : null}

      <div className="border-t border-base-100" />
    </div>
  );
};

export default AuthorCards;
